import { RowCrudStatus } from "../../common/types";
import {
  QueryPattern,
  QueryPatternCreateMutationVariables,
  QueryPatternUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { EvaluationPathType } from "../../../api/graphql";
import { safeJsonParse } from "../../../common/utils";
import { QueryPatternSource } from "../../../api/graphql";
import { blankToNull } from "../../../common/blankToNull";

export type QueryPatternValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  name: string;
  pattern: string;
  pathType: EvaluationPathType;
  variables: string;
  toolTemplate: string;
  argsTemplate: string;
  variations: string;
  exampleInstantiation: string;
  source: QueryPatternSource;
  qualityScore: number;
  usageCount: number;
  isActive: boolean;
  notes: string;
};

export type QueryPatternErrorsType = {
  name?: { message: string; fieldLabel: string };
  pattern?: { message: string; fieldLabel: string };
  pathType?: { message: string; fieldLabel: string };
  variables?: { message: string; fieldLabel: string };
  toolTemplate?: { message: string; fieldLabel: string };
  argsTemplate?: { message: string; fieldLabel: string };
  variations?: { message: string; fieldLabel: string };
  exampleInstantiation?: { message: string; fieldLabel: string };
  source?: { message: string; fieldLabel: string };
  qualityScore?: { message: string; fieldLabel: string };
  usageCount?: { message: string; fieldLabel: string };
  isActive?: { message: string; fieldLabel: string };
  notes?: { message: string; fieldLabel: string };
};

export function QueryPatternValuesInit(): QueryPatternValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    name: "",
    pattern: "",
    pathType: "" as EvaluationPathType,
    variables: "{}",
    toolTemplate: "",
    argsTemplate: "{}",
    variations: "{}",
    exampleInstantiation: "",
    source: "auto_generated" as QueryPatternSource,
    qualityScore: 3,
    usageCount: 0,
    isActive: false,
    notes: "",
  };
}

export type QueryPatternType = QueryPattern | QueryPatternValuesType;

export function queryPatternDisplayId(
  source: Partial<QueryPattern>,
  _langStore: LangStore
) {
  return source.name ?? _langStore.emptyStringSymbol;
}

export function getQueryPatternValues(
  source: QueryPatternType,
  _langStore: LangStore,
  _duplicated: boolean = false
): QueryPatternValuesType {
  const entityValues = QueryPatternValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : queryPatternDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.name = source.name;
  entityValues.pattern = source.pattern;
  entityValues.pathType = source.pathType;
  entityValues.variables = source.variables;
  entityValues.toolTemplate = source.toolTemplate;
  entityValues.argsTemplate = source.argsTemplate;
  entityValues.variations = source.variations;
  entityValues.exampleInstantiation = source.exampleInstantiation;
  entityValues.source = source.source;
  entityValues.qualityScore = source.qualityScore || 0;
  entityValues.usageCount = source.usageCount || 0;
  entityValues.isActive = source.isActive || false;
  entityValues.notes = source.notes || "";
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getQueryPatternUpdateVariables(
  values: QueryPatternValuesType
): QueryPatternUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    name: values.name,
    pattern: values.pattern,
    pathType: values.pathType,
    variables: safeJsonParse(values.variables, "variables"),
    toolTemplate: values.toolTemplate,
    argsTemplate: safeJsonParse(values.argsTemplate, "argsTemplate"),
    variations: safeJsonParse(values.variations, "variations"),
    exampleInstantiation: values.exampleInstantiation,
    source: values.source,
    qualityScore: values.qualityScore,
    usageCount: values.usageCount,
    isActive: values.isActive,
    notes: blankToNull(values.notes),
  };
}

export function getQueryPatternCreateVariables(
  values: QueryPatternValuesType
): QueryPatternCreateMutationVariables {
  return {
    name: values.name,
    pattern: values.pattern,
    pathType: values.pathType,
    variables: safeJsonParse(values.variables, "variables"),
    toolTemplate: values.toolTemplate,
    argsTemplate: safeJsonParse(values.argsTemplate, "argsTemplate"),
    variations: safeJsonParse(values.variations, "variations"),
    exampleInstantiation: values.exampleInstantiation,
    source: values.source,
    qualityScore: values.qualityScore,
    usageCount: values.usageCount,
    isActive: values.isActive,
    notes: blankToNull(values.notes),
  };
}
